/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import { Component, ChangeDetectionStrategy, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Observable, Subject, filter, take, takeUntil } from 'rxjs';
import * as appStrings from '@touchplan/strings';
import { ImportTicketsStateService } from '../../services/import-tickets-state.service';
import { ITicketImportResponseMetadata, ImportResponse } from '@touchplan/common';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import { MatDividerModule } from '@angular/material/divider';

@Component({
	selector: 'import-tickets-results-step',
	standalone: true,
	templateUrl: './import-tickets-results-step.component.html',
	styleUrls: ['./import-tickets-results-step.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [CommonModule, MatDialogModule, MatButtonModule, PresentationModule, MatDividerModule],
})
export class ImportTicketsResultsStepComponent implements OnInit, OnDestroy {
	public appStrings = appStrings;

	protected importResults$: Observable<ITicketImportResponseMetadata> = this.importTicketsStateService.select(
		(s) => s.importResponseMetadata
	);

	private shutdownSubject$ = new Subject<void>();

	ngOnInit() {
		this.logAnalyticsResults();
	}

	private logAnalyticsResults(): void {
		this.importTicketsStateService
			.select((state) => ({
				planId: state.planId,
				variancesCount:
					!!state.variances && typeof state.variances === 'object'
						? Object.entries(state.variances)?.length
						: 0,
				importType: state.importType,
				importFileFormat: state.importFileFormat,
				scope: state.importScope,
				importResponseMetadata: state.importResponseMetadata,
			}))
			.pipe(
				filter((state) => !!state.importResponseMetadata),
				take(1),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((importExecuted) => {
				let importMetadata: string;
				try {
					importMetadata = JSON.stringify({
						format: importExecuted.importFileFormat,
						scope: importExecuted.scope,
						ticketsCreated: importExecuted.importResponseMetadata.tickets?.created,
						ticketsUpdated: importExecuted.importResponseMetadata.tickets?.updated,
						varianceReasonTickets: importExecuted.variancesCount,
						precedenceSet: importExecuted.importResponseMetadata.dependencies?.created,
					});
				} catch (e) {
					importMetadata = '';
				}
				analyticsEventsService.ticketImportUpdateExport(
					importExecuted.importType,
					!!importExecuted.importResponseMetadata.errorDescription
						? ImportResponse.error
						: ImportResponse.success,
					importExecuted.planId,
					importMetadata
				);
			});
	}

	public ngOnDestroy(): void {
		this.shutdownSubject$.next();
		this.shutdownSubject$.complete();
	}

	constructor(private importTicketsStateService: ImportTicketsStateService) {}
}
